/// <amd-module name="VSS/Platform/Trace" />
import { IAuthorizationTokenProvider, IVssPageContext, IVssRestClientOptions, IVssService, ServiceOptions, Services, VssService } from "./Context";
import { ClientTraceEvent, Level } from "./Contracts/ClientTrace";
import { getCurrentRoute } from "./Navigation";
import { IClientTraceRestClient } from "./RestClient/ClientTrace";
import { executeWithSilentAuth } from "./TokenProvider";

/**
 * Export syntactic-sugar functions for convenient one-line traces for consumers
 */

/**
 * Trace an event to the server at `error` level.
 *
 * @param pageContext - The IVssPageContext to use to obtain the tracing service
 *
 * @param area - The area defines a large scope of features under a unique name.
 *
 * @param feature - The feature defines a fine grained scope of a single feature.
 *  It should be unique within the area, and the properties should be used to
 *  distinguish how the feature was used.
 *
 * @param message The message to trace to the server
 *
 * @param properties - An dictionary of name/value pairs with details about the
 *  event. This MAY contain simple or complex values.
 */
export function traceError(pageContext: IVssPageContext, area: string, feature: string, message: string, properties?: { [key: string]: any }): void {
    const traceService = pageContext.getService<IVssTraceService>("IVssTraceService");
    traceService.traceError(area, feature, message, properties);
}

/**
 * Trace an exception to the server at `error` level.
 *
 * @param pageContext - The IVssPageContext to use to obtain the tracing service
 *
 * @param area - The area defines a large scope of features under a unique name.
 *
 * @param feature - The feature defines a fine grained scope of a single feature.
 *  It should be unique within the area, and the properties should be used to
 *  distinguish how the feature was used.
 *
 * @param exception The exception to trace to the server
 *
 * @param properties - An dictionary of name/value pairs with details about the
 *  event. This MAY contain simple or complex values.
 */
export function traceException(
    pageContext: IVssPageContext,
    area: string,
    feature: string,
    exception: Error | string,
    properties?: { [key: string]: any }
): void {
    const traceService = pageContext.getService<IVssTraceService>("IVssTraceService");
    traceService.traceException(area, feature, exception, properties);
}

/**
 * Trace an event to the server at `warning` level.
 *
 * @param pageContext - The IVssPageContext to use to obtain the tracing service
 *
 * @param area - The area defines a large scope of features under a unique name.
 *
 * @param feature - The feature defines a fine grained scope of a single feature.
 *  It should be unique within the area, and the properties should be used to
 *  distinguish how the feature was used.
 *
 * @param message The message to trace to the server
 *
 * @param properties - An dictionary of name/value pairs with details about the
 *  event. This MAY contain simple or complex values.
 */
export function traceWarning(
    pageContext: IVssPageContext,
    area: string,
    feature: string,
    message: string,
    properties?: { [key: string]: any }
): void {
    const traceService = pageContext.getService<IVssTraceService>("IVssTraceService");
    traceService.traceWarning(area, feature, message, properties);
}

/**
 * Trace an event to the server.
 *
 * @param pageContext - The IVssPageContext to use to obtain the tracing service
 *
 * @param event - The event to send to the server.
 */
export function trace(pageContext: IVssPageContext, event: ClientTraceEvent): void {
    const traceService = pageContext.getService<IVssTraceService>("IVssTraceService");
    traceService.trace(event);
}

/**
 * The trace service is used to trace diagnostic information back to the hosting service.
 */
export interface IVssTraceService extends IVssService {
    /**
     * Trace an event to the server at `error` level.
     *
     * @param area - The area defines a large scope of features under a unique name.
     *
     * @param feature - The feature defines a fine grained scope of a single feature.
     *  It should be unique within the area, and the properties should be used to
     *  distinguish how the feature was used.
     *
     * @param message The message to trace to the server
     *
     * @param properties - An dictionary of name/value pairs with details about the
     *  event. This MAY contain simple or complex values.
     */
    traceError(area: string, feature: string, message: string, properties?: { [key: string]: any }): void;

    /**
     * Trace an exception to the server at `error` level.
     *
     * @param area - The area defines a large scope of features under a unique name.
     *
     * @param feature - The feature defines a fine grained scope of a single feature.
     *  It should be unique within the area, and the properties should be used to
     *  distinguish how the feature was used.
     *
     * @param exception The exception to trace to the server
     *
     * @param properties - An dictionary of name/value pairs with details about the
     *  event. This MAY contain simple or complex values.
     */
    traceException(area: string, feature: string, exception: Error | string, properties?: { [key: string]: any }): void;

    /**
     * Trace an event to the server at `warning` level.
     *
     * @param area - The area defines a large scope of features under a unique name.
     *
     * @param feature - The feature defines a fine grained scope of a single feature.
     *  It should be unique within the area, and the properties should be used to
     *  distinguish how the feature was used.
     *
     * @param message The message to trace to the server
     *
     * @param properties - An dictionary of name/value pairs with details about the
     *  event. This MAY contain simple or complex values.
     */
    traceWarning(area: string, feature: string, message: string, properties?: { [key: string]: any }): void;

    /**
     * Trace an event to the server.
     *
     * @param event - The event to send to the server.
     */
    trace(event: ClientTraceEvent): void;
}

const MAX_TRACED_GLOBAL_ERRORS = 5;

class VssTraceService extends VssService implements IVssTraceService {
    private publishInterval: number;
    private events: ClientTraceEvent[] = [];
    private globalErrorCount = 0;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        // Process any errors that were captured prior to the VssTraceService starting
        if (onPreTraceServiceError) {
            window.removeEventListener("error", onPreTraceServiceError);
        }

        if (preTraceServiceStartErrors && preTraceServiceStartErrors.length > 0) {
            for (const event of preTraceServiceStartErrors) {
                this._onWindowError(event);
            }
            // clear the errors to ensure they are never processed twice
            preTraceServiceStartErrors.length = 0;
        }

        // Setup an interval that is used to push any trace events that have been
        // published since we last published them.
        this.publishInterval = window.setInterval(this._sendEvents.bind(this), 1000);

        window.addEventListener("error", this._onWindowError);
        window.addEventListener("unhandledrejection", this._onUnhandledRejection);
    }

    public _serviceRestart(pageContext: IVssPageContext) {
        super._serviceRestart(pageContext);

        this.globalErrorCount = 0;

        // Send any pending events just before we restart with the new page.
        this._sendEvents();
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        // Make sure our timed publishing of events no longer happens
        if (this.publishInterval) {
            window.clearInterval(this.publishInterval);
            this.publishInterval = 0;
        }

        window.removeEventListener("error", this._onWindowError);
        window.removeEventListener("unhandledrejection", this._onUnhandledRejection);

        super._serviceEnd(pageContext);
    }

    /**
     * Trace an event to the server at `error` level.
     *
     * @param area - The area defines a large scope of features under a unique name.
     *
     * @param feature - The feature defines a fine grained scope of a single feature.
     *  It should be unique within the area, and the properties should be used to
     *  distinguish how the feature was used.
     *
     * @param message The message to trace to the server
     *
     * @param properties - An dictionary of name/value pairs with details about the
     *  event. This MAY contain simple or complex values.
     */
    public traceError(area: string, feature: string, message: string, properties?: { [key: string]: any }): void {
        const event = {
            area,
            feature,
            message,
            properties,
            level: Level.Error
        } as ClientTraceEvent;

        this.trace(event);
    }

    /**
     * Trace an exception to the server at `error` level.
     *
     * @param area - The area defines a large scope of features under a unique name.
     *
     * @param feature - The feature defines a fine grained scope of a single feature.
     *  It should be unique within the area, and the properties should be used to
     *  distinguish how the feature was used.
     *
     * @param exception The exception to trace to the server
     */
    public traceException(area: string, feature: string, exception: Error | string, properties?: { [key: string]: any }): void {
        const event = {
            area,
            feature,
            message: exception ? (exception as Error).message : exception || "unknown",
            exceptionType: "",
            properties: properties,
            level: Level.Warning
        } as ClientTraceEvent;

        if (exception) {
            if (!event.properties) {
                event.properties = {};
            }
            event.properties["stackTrace"] = (exception as Error).stack;
            event.exceptionType = (exception as Error).name;
        }

        this.trace(event);
    }

    /**
     * Trace an event to the server at `warning` level.
     *
     * @param area - The area defines a large scope of features under a unique name.
     *
     * @param feature - The feature defines a fine grained scope of a single feature.
     *  It should be unique within the area, and the properties should be used to
     *  distinguish how the feature was used.
     *
     * @param message The message to trace to the server
     *
     * @param properties - An dictionary of name/value pairs with details about the
     *  event. This MAY contain simple or complex values.
     */
    public traceWarning(area: string, feature: string, message: string, properties?: { [key: string]: any }): void {
        const event = {
            area,
            feature,
            message,
            properties,
            level: Level.Warning
        } as ClientTraceEvent;

        this.trace(event);
    }

    /**
     * Trace an event to the server.
     *
     * @param event - The event to send to the server.
     */
    public trace(event: ClientTraceEvent): void {
        this.events.push(event);
    }

    private _sendEvents(): void {
        if (this.events.length > 0) {
            executeWithSilentAuth(this.pageContext, (provider?: IAuthorizationTokenProvider) => this._sendEventsImpl(provider));
        }
    }

    private _sendEventsImpl(authTokenProvider?: IAuthorizationTokenProvider) {
        const localEvents = this.events;
        this.events = [];

        if (localEvents.length > 0) {
            let vssRestClientOptions: IVssRestClientOptions | undefined;
            if (!!authTokenProvider) {
                vssRestClientOptions = {
                    authTokenProvider: authTokenProvider
                };
            }

            const traceClient = this.pageContext.getRestClient<IClientTraceRestClient>("IClientTraceRestClient", vssRestClientOptions);
            traceClient.publishEvents(localEvents)
                .catch((error) => console.log(error));
        }
    }

    private _onWindowError = (event: ErrorEvent): void => {
        if (event.filename && this.globalErrorCount++ < MAX_TRACED_GLOBAL_ERRORS) {
            const properties = {
                filename: event.filename,
                lineNumber: event.lineno,
                columnNumber: event.colno,
                errorType: "UnhandledException"
            };

            this.traceException("WebUnhandledExceptions", getCurrentRoute(this.pageContext).routeId, event.error, properties);
        }
    };

    private _onUnhandledRejection = (event: PromiseRejectionEvent): void => {
        const reason = event.reason;

        if ($DEBUG) {
            // Cancelable promises should handle rejection, warn if not
            if (reason && reason.isCanceled) {
                console.warn(`Unhandled rejection of a canceled promise. Please handle rejection. Reason: `, reason);
            }
        }

        if (reason && !reason.isCanceled) {
            console.warn(`Unhandled promise rejection: ${reason}${reason.stack ? "\\n" + reason.stack : ""}`);

            if (this.globalErrorCount++ < MAX_TRACED_GLOBAL_ERRORS) {
                this.traceException("WebUnhandledExceptions", getCurrentRoute(this.pageContext).routeId, reason, {
                    errorType: "UnhandledRejection"
                });
            }
        }
    };
}

Services.add("IVssTraceService", { serviceFactory: VssTraceService, options: ServiceOptions.Persistant | ServiceOptions.Startup });

/**
 * Errors captured prior to VSSTraceService starting.  See ContentProviderElement.cs
 */
declare var preTraceServiceStartErrors: any;
declare var onPreTraceServiceError: any;
